import type { Video, Media } from '@/payload-types'
import { isPopulatedObject } from '@/lib/utils'
import { getYoutubeIdFromInput, ytFallbackThumbnailUrl, ytIdToThumbnailUrl } from '@/lib/youtube'

// Helper to convert duration strings like "1:50" or "2:05:10" to seconds
function durationToSeconds(duration?: string | null): number {
  if (!duration) return 0
  const parts = duration.split(':').map((part) => {
    const parsed = parseInt(part, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  })
  // Support h:m:s or m:s or s
  let seconds = 0
  let multiplier = 1
  while (parts.length) {
    const value = parts.pop() as number
    seconds += value * multiplier
    multiplier *= 60
  }

  return seconds
}

// Filters videos by unique video source and duration greater than 60 seconds
export function filterUniqueAndLongVideos(videos: Video[], minSeconds = 60): Video[] {
  const seen = new Set<string>()
  return videos.filter((video) => {
    const sourceKey = getVideoSourceKey(video)
    if (!sourceKey) return false
    if (durationToSeconds(video.duration) <= minSeconds) return false
    if (seen.has(sourceKey)) return false
    seen.add(sourceKey)
    return true
  })
}

export type VideoThumbnailSource = 'override' | 'youtube' | 'mux' | 'local'

export type ResolvedVideoThumbnail = {
  url: string
  source: VideoThumbnailSource
}

// Resolve the thumbnail for a video using the canonical priority:
//   1. thumbnailOverride (admin-uploaded override always wins)
//   2. YouTube/Mux source thumbnail (default — kept in sync with the platform)
//   3. local thumbnail field (final fallback for non-YouTube/Mux sources)
// `source` lets render-side callers decide whether Next.js Image optimization
// applies (own-CDN uploads) or should be skipped (external CDN like YouTube).
export function getThumbnail(
  video: Pick<Video, 'url' | 'youtubeId' | 'thumbnail' | 'thumbnailOverride'>,
): ResolvedVideoThumbnail | null {
  if (isPopulatedObject<Media>(video.thumbnailOverride) && video.thumbnailOverride.url) {
    return { url: video.thumbnailOverride.url, source: 'override' }
  }

  const { thumbnailUrl: sourceThumbnailUrl, sourceKey } = getVideoSource(video)
  if (sourceThumbnailUrl) {
    const source: VideoThumbnailSource = sourceKey?.startsWith('mux:') ? 'mux' : 'youtube'
    return { url: sourceThumbnailUrl, source }
  }

  if (isPopulatedObject<Media>(video.thumbnail) && video.thumbnail.url) {
    return { url: video.thumbnail.url, source: 'local' }
  }

  return null
}

// Normalizes a Video relationship field (populated Media object, raw ID, or
// null/undefined) into the minimal shape carried by chip/play-request payloads.
// Chip data (ResolvedVideoSegment, ResolvedVideoApp, SegmentPlayRequest) only
// ever needs { id, url }, not the full Media object.
export function normalizeThumbnailRef(
  value: Media | number | null | undefined,
): { id: number; url: string } | null {
  if (isPopulatedObject<Media>(value) && value.url) {
    return { id: value.id, url: value.url }
  }
  return null
}

// Returns the canonical thumbnail URL for use in JSON-LD VideoObject schema and
// other server-rendered metadata. YouTube URLs are normalized to hqdefault.jpg
// (the always-exists variant) so crawlers don't see 404s on the rare hq720 miss.
export function getThumbnailUrl(
  video: Pick<Video, 'url' | 'youtubeId' | 'thumbnail' | 'thumbnailOverride'>,
): string | null {
  const thumbnail = getThumbnail(video)
  if (!thumbnail) return null
  return thumbnail.source === 'youtube' ? ytFallbackThumbnailUrl(thumbnail.url) : thumbnail.url
}

// Override + local thumbnail uploads are served from our own CDN — Next.js can
// resize them and emit AVIF/WebP. YouTube/Mux thumbnails come from external
// CDNs and are already optimized JPEGs, so skip the /_next/image round trip.
export function shouldOptimizeThumbnail(source: VideoThumbnailSource): boolean {
  return source === 'override' || source === 'local'
}

export function getVideoSource(video: Pick<Video, 'url' | 'youtubeId'>): {
  embedUrl: string | null
  thumbnailUrl: string | null
  sourceKey: string | null
} {
  const youtubeIdFromField = getYoutubeIdFromInput(video.youtubeId)
  if (youtubeIdFromField) {
    return {
      embedUrl: `https://www.youtube.com/embed/${youtubeIdFromField}?autoplay=1`,
      thumbnailUrl: ytIdToThumbnailUrl({ id: youtubeIdFromField }),
      sourceKey: `youtube:${youtubeIdFromField}`,
    }
  }

  const youtubeIdFromUrl = getYoutubeIdFromInput(video.url)
  if (youtubeIdFromUrl) {
    return {
      embedUrl: `https://www.youtube.com/embed/${youtubeIdFromUrl}?autoplay=1`,
      thumbnailUrl: ytIdToThumbnailUrl({ id: youtubeIdFromUrl }),
      sourceKey: `youtube:${youtubeIdFromUrl}`,
    }
  }

  const muxPlaybackId = getMuxPlaybackIdFromUrl(video.url)
  if (muxPlaybackId) {
    const iframeSrc = new URL(`https://player.mux.com/${muxPlaybackId}`)
    iframeSrc.searchParams.set('autoplay', '1')
    iframeSrc.searchParams.set('muted', '1')

    return {
      embedUrl: iframeSrc.toString(),
      thumbnailUrl: `https://image.mux.com/${muxPlaybackId}/thumbnail.png`,
      sourceKey: `mux:${muxPlaybackId}`,
    }
  }

  return {
    embedUrl: null,
    thumbnailUrl: null,
    sourceKey: video.url ? `url:${video.url}` : null,
  }
}

function getMuxPlaybackIdFromUrl(rawUrl?: string | null) {
  if (!rawUrl || typeof rawUrl !== 'string') return null

  try {
    const parsedUrl = new URL(rawUrl)
    const host = parsedUrl.hostname.toLowerCase()

    if (host !== 'mux.com' && !host.endsWith('.mux.com')) {
      return null
    }

    const muxPlaybackFromQuery =
      parsedUrl.searchParams.get('playbackId') || parsedUrl.searchParams.get('playback_id')

    if (muxPlaybackFromQuery) {
      return muxPlaybackFromQuery
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    if (pathParts.length === 0) {
      return null
    }

    const playbackCandidate = pathParts[pathParts.length - 1]

    if (!playbackCandidate) {
      return null
    }

    return playbackCandidate.replace(/\.[^/.]+$/, '')
  } catch {
    return null
  }
}

export function getVideoSourceKey(video: Pick<Video, 'url' | 'youtubeId'>): string | null {
  return getVideoSource(video).sourceKey
}
